import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared chrome for standalone legal documents (Terms, Privacy Policy).
 *
 * These pages render outside the app's MainLayout/AuthLayout so they are
 * readable in any auth state — including from the registration form, where a
 * prospective user has no account yet. The layout is intentionally
 * self-contained (like NotFoundPage).
 */

interface LegalPageProps {
  title: string;
  /** Human-readable "last updated" date shown under the title. */
  lastUpdated: React.ReactNode;
  children: React.ReactNode;
}

const LegalPage: React.FC<LegalPageProps> = ({ title, lastUpdated, children }) => {
  return (
    <div className="min-h-screen bg-white">
      {/* Simple top bar with brand + back-to-register affordance */}
      <header className="border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-primary-600">
            Sulis-Liiga
          </Link>
          <Link
            to="/register"
            className="text-sm font-medium text-primary-600 hover:text-primary-500"
          >
            &larr; Back to registration
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
          {title}
        </h1>
        <p className="mt-2 text-sm text-gray-500">Last updated: {lastUpdated}</p>

        {/* Template / not-legal-advice banner. Remove once a qualified adviser
            has reviewed and approved the final text. */}
        <div className="mt-6 rounded-md border border-warning-300 bg-warning-50 p-4">
          <p className="text-sm text-warning-800">
            <strong>Template — pending legal review.</strong> This document is a starting template
            and does not constitute legal advice. Every item marked{' '}
            <code className="rounded bg-warning-100 px-1 py-0.5 text-warning-900">
              [PLACEHOLDER: …]
            </code>{' '}
            must be completed, and the whole text reviewed by a qualified data-protection / legal
            adviser, before this app is used with real users.
          </p>
        </div>

        <div className="mt-8 space-y-8">{children}</div>

        <footer className="mt-16 border-t border-gray-200 pt-6">
          <nav className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            <Link to="/terms" className="font-medium text-gray-500 hover:text-gray-700">
              Terms and Conditions
            </Link>
            <span className="border-l border-gray-300" aria-hidden="true" />
            <Link to="/privacy" className="font-medium text-gray-500 hover:text-gray-700">
              Privacy Policy
            </Link>
            <span className="border-l border-gray-300" aria-hidden="true" />
            <Link to="/" className="font-medium text-gray-500 hover:text-gray-700">
              Home
            </Link>
          </nav>
        </footer>
      </main>
    </div>
  );
};

/** A numbered/titled section within a legal document. */
export const Section: React.FC<{ heading: string; children: React.ReactNode }> = ({
  heading,
  children,
}) => (
  <section className="space-y-3">
    <h2 className="text-xl font-semibold text-gray-900">{heading}</h2>
    {children}
  </section>
);

/** A body paragraph with consistent legal-doc typography. */
export const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-base leading-relaxed text-gray-700">{children}</p>
);

/** A bulleted list of points. */
export const List: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul className="list-disc space-y-2 pl-6 text-base leading-relaxed text-gray-700">{children}</ul>
);

/** Inline placeholder marker for values the operator must supply. */
export const Placeholder: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded bg-warning-100 px-1 py-0.5 font-mono text-sm text-warning-900">
    [PLACEHOLDER: {children}]
  </span>
);

export default LegalPage;
